import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const ensureUserProfile = async (authUser) => {
    if (!authUser?.id || !authUser?.email) return;

    const fallbackName = authUser.email.split('@')[0];
    const fullName = authUser.user_metadata?.full_name || fallbackName;
    const role = authUser.user_metadata?.role || 'client';

    const { error } = await supabase.from('user_profiles').upsert({
      id: authUser.id,
      email: authUser.email.toLowerCase().trim(),
      full_name: fullName,
      role,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'id' });

    if (error) {
      console.error('Failed to upsert user profile during auth sync:', error);
    }
  };

  const buildUser = (authUser) => {
    // Build user ONLY from metadata — instant, no DB, never fails
    // Profile page will load the rich data separately from user_profiles
    return {
      id: authUser.id,
      email: authUser.email,
      name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
      role: authUser.user_metadata?.role || 'client',
      photo_url: null,
    };
  };

  useEffect(() => {
    let mounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;

        if (session?.user) {
          // Synchronous — no async, no DB call, never hangs
          const userData = buildUser(session.user);
          setUser(userData);
          setIsAuthenticated(true);
          // Fire-and-forget profile sync to ensure message/profile lookups always resolve by user_profiles.id.
          ensureUserProfile(session.user);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }

        setLoading(false);
      }
    );

    // Safety net: if callback never fires, stop loading after 2s
    const timeout = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 2000);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const login = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('authToken');
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};